import { motion } from "framer-motion";
import { APP_NAME, SLOGAN } from "@/lib/branding";
import { cn } from "@/lib/utils";
import { BrandingSVG } from "./BrandingSVG";
import { Slogan } from "./Slogan";

type LogoVariant = "mark" | "horizontal" | "stacked";

interface LogoProps {
  variant?: LogoVariant;
  /** Size of the "99" mana symbol in px. */
  size?: number;
  /** Optional styling for the badge independently of the wordmark. */
  markClassName?: string;
  /** Vertical badge offset in px, independent of the wordmark. */
  markOffsetY?: number;
  /** Vertical offset for the wordmark and slogan as a group. */
  textOffsetY?: number;
  /** Show "..but a Deck ain't one" under the wordmark. */
  showSlogan?: boolean;
  /** Place the slogan inline to the right of the wordmark. */
  inlineSlogan?: boolean;
  animated?: boolean;
  className?: string;
  wordmarkClassName?: string;
  sloganClassName?: string;
}

/**
 * The 99 Problems logo: the golden crown badge, optionally with the
 * "Problems" wordmark and slogan beside or below it.
 */
export function Logo({
  variant = "horizontal",
  size = 40,
  markClassName,
  markOffsetY,
  textOffsetY,
  showSlogan = true,
  inlineSlogan = false,
  animated = false,
  className,
  wordmarkClassName,
  sloganClassName,
}: LogoProps) {
  const mark = (
    <motion.span
      className={cn("brand-glow-hover inline-flex shrink-0 rounded-full", markClassName)}
      style={markOffsetY === undefined ? undefined : { position: "relative", top: markOffsetY }}
      whileHover={{ scale: 1.08, rotate: -8 }}
      whileTap={{ scale: 0.95 }}
      // whileTap makes Framer add tabIndex=0; the logo is decorative or wrapped in a link.
      tabIndex={-1}
      transition={{ type: "spring", stiffness: 260, damping: 14 }}
    >
      <BrandingSVG
        size={size}
        animated={animated}
        compact={size < 64}
        title={variant === "mark" ? APP_NAME : undefined}
      />
    </motion.span>
  );

  if (variant === "mark") {
    return <span className={cn("inline-flex", className)}>{mark}</span>;
  }

  const stacked = variant === "stacked";
  return (
    <span
      className={cn(
        "group inline-flex",
        stacked ? "flex-col items-center gap-3 text-center" : "items-center gap-2.5",
        className,
      )}
      aria-label={`${APP_NAME} — ${SLOGAN}`}
      role="img"
    >
      {mark}
      <span
        className={cn(
          "flex leading-none",
          inlineSlogan ? "flex-row items-center gap-2.5" : "flex-col",
          stacked && "items-center",
        )}
        style={textOffsetY === undefined ? undefined : { position: "relative", top: textOffsetY }}
      >
        <span className={cn("brand-wordmark text-lg", wordmarkClassName)}>Problems</span>
        {showSlogan && (
          <Slogan className={cn(inlineSlogan ? "text-xs" : "mt-1 text-xs", sloganClassName)} />
        )}
      </span>
    </span>
  );
}
